/**
 * Starter theme-set proposal (#845, PR 4; docs/plans/2026-10-02-conversation-themes.md
 * §2 Р1, §4) — the contracts of `/api/v1/analytics/voice/themes/bootstrap`, of
 * the internal workflows-api route that calls the model, of the model's answer
 * and of what `org_theme_bootstraps.result_json` stores.
 *
 * The limits and windows are `@aica/core/voc/theme-bootstrap` (the pure part
 * shared by workflows-api and edge-api) — re-exported here, not repeated.
 */
import { z } from 'zod/v4';
import {
  THEME_BOOTSTRAP_CALL_TIMEOUT_MS,
  THEME_BOOTSTRAP_CLIENT_TIMEOUT_MS,
  THEME_BOOTSTRAP_FAILED_RETRY_MS,
  THEME_BOOTSTRAP_MAX_PHRASES,
  THEME_BOOTSTRAP_MAX_THEMES,
  THEME_BOOTSTRAP_MIN_THEMES,
  THEME_BOOTSTRAP_PAIRS_RETENTION_DAYS,
  THEME_BOOTSTRAP_PROXY_DEADLINE_MS,
  THEME_BOOTSTRAP_REGENERATE_MS,
  THEME_BOOTSTRAP_RUNNING_STALE_MS,
  THEME_BOOTSTRAP_LAST_CALL_CLAIM_AGE_MS,
  THEME_BOOTSTRAP_STEP_TIMEOUT_MS,
  THEME_BOOTSTRAP_UNPAID_FAILURE_CODES,
  THEME_BOOTSTRAP_WINDOW_DAYS,
} from '@aica/core/voc/theme-bootstrap';
import {
  ORG_THEME_DEFINITION_MAX,
  ORG_THEME_NAME_MAX,
  orgThemeDefinitionSchema,
  orgThemeNameSchema,
  orgThemeSchema,
  orgThemeBootstrapStatusSchema,
} from './org-themes';

export {
  THEME_BOOTSTRAP_CALL_TIMEOUT_MS,
  THEME_BOOTSTRAP_CLIENT_TIMEOUT_MS,
  THEME_BOOTSTRAP_FAILED_RETRY_MS,
  THEME_BOOTSTRAP_MAX_PHRASES,
  THEME_BOOTSTRAP_MAX_THEMES,
  THEME_BOOTSTRAP_MIN_THEMES,
  THEME_BOOTSTRAP_PAIRS_RETENTION_DAYS,
  THEME_BOOTSTRAP_PROXY_DEADLINE_MS,
  THEME_BOOTSTRAP_REGENERATE_MS,
  THEME_BOOTSTRAP_RUNNING_STALE_MS,
  THEME_BOOTSTRAP_LAST_CALL_CLAIM_AGE_MS,
  THEME_BOOTSTRAP_STEP_TIMEOUT_MS,
  THEME_BOOTSTRAP_UNPAID_FAILURE_CODES,
  THEME_BOOTSTRAP_WINDOW_DAYS,
};

// ============================================================================
// Stored result — `org_theme_bootstraps.result_json`
// ============================================================================

/** One proposed theme as stored: the minimized text and the phrase numbers. */
export const themeBootstrapStoredThemeSchema = z.object({
  name: z.string().min(1).max(ORG_THEME_NAME_MAX),
  definition: z.string().max(ORG_THEME_DEFINITION_MAX),
  /** The model's definition did not survive minimization — «to refine». */
  needsDefinition: z.boolean(),
  /** 1-based numbers into `pairs`, as the model grouped them. */
  phrases: z.array(z.number().int().min(1)).max(THEME_BOOTSTRAP_MAX_PHRASES),
});

/**
 * `result_json` of a `ready` attempt. `pairs` — the numbered `(normalized
 * topic, call_category)` pairs sent to the model (number = position + 1):
 * derived history data, so it is erased (the key disappears) when the
 * proposal is approved, regenerated, or 30 days old. Without it the screen
 * shows the themes without shares.
 */
export const themeBootstrapStoredResultSchema = z.object({
  promptRevision: z.string().min(1),
  /** Language the names and definitions were requested in. */
  language: z.string().min(1),
  /** The history window, half-open `[windowStart, windowEnd)`. */
  windowStart: z.string().min(1),
  windowEnd: z.string().min(1),
  /** The history did not fit and was sampled by category. */
  sampled: z.boolean(),
  themes: z.array(themeBootstrapStoredThemeSchema).max(THEME_BOOTSTRAP_MAX_THEMES),
  pairs: z
    .array(z.tuple([z.string().min(1), z.string().min(1)]))
    .max(THEME_BOOTSTRAP_MAX_PHRASES)
    .optional(),
  approvedAt: z.string().optional(),
});

export type ThemeBootstrapStoredTheme = z.infer<typeof themeBootstrapStoredThemeSchema>;
export type ThemeBootstrapStoredResult = z.infer<typeof themeBootstrapStoredResultSchema>;

/**
 * The one reader of a `result_json` column: `null` when the column is empty,
 * not JSON, or does not match the schema. Every stored shape below goes
 * through it.
 */
function parseJsonColumn<T extends z.ZodType>(
  schema: T,
  json: string | null
): z.infer<T> | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json ?? 'null');
  } catch {
    return null;
  }
  const parsed = schema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * The stored result of a `ready` row as the contract reads it; `null` when it
 * is not JSON or does not match. The one reader for both apps: edge-api shows
 * a row that fails here as unreadable, and workflows-api lets it be claimed
 * again at once.
 */
export function parseThemeBootstrapStoredResult(
  resultJson: string | null
): ThemeBootstrapStoredResult | null {
  return parseJsonColumn(themeBootstrapStoredResultSchema, resultJson);
}

/**
 * `result_json` of a `running` attempt whose paid call has STARTED: written by
 * the Workflow right before `model.call`, in one statement fenced by the claim
 * token. It is the durable answer to «was money possibly spent?» that the
 * Workflow's own memory cannot give (a step timeout or a retry loses it): a
 * failure close and a retried step read it. No phrase, no count — the time
 * only. Every reader of a `running` row treats it as «running, no result»; the
 * close of the attempt overwrites the column.
 */
export const themeBootstrapCallStartedSchema = z.object({
  callStartedAt: z.string().min(1),
});

export type ThemeBootstrapCallStarted = z.infer<typeof themeBootstrapCallStartedSchema>;

export function serializeThemeBootstrapCallStarted(startedAt: Date): string {
  const marker: ThemeBootstrapCallStarted = { callStartedAt: startedAt.toISOString() };
  return JSON.stringify(marker);
}

/** The marker of a `running` row; `null` when there is none (the call did not start). */
export function parseThemeBootstrapCallStarted(
  resultJson: string | null
): ThemeBootstrapCallStarted | null {
  return parseJsonColumn(themeBootstrapCallStartedSchema, resultJson);
}

/**
 * `result_json` of a `failed/no_history` attempt: how many conversations the
 * redaction set aside, so the screen can say «nothing could be sent» with the
 * number instead of repeating the intro. The one writer
 * (`serializeThemeBootstrapNoHistoryResult`, workflows-api) and the one reader
 * (`parseThemeBootstrapNoHistoryResult`, edge-api) share this shape.
 */
export const themeBootstrapNoHistoryResultSchema = z.object({
  withheld: z.number().int().min(0),
});

export type ThemeBootstrapNoHistoryResult = z.infer<
  typeof themeBootstrapNoHistoryResultSchema
>;

export function serializeThemeBootstrapNoHistoryResult(withheld: number): string {
  const result: ThemeBootstrapNoHistoryResult = { withheld };
  return JSON.stringify(result);
}

/**
 * The count of a `failed/no_history` row; `null` when the column is empty, not
 * JSON or does not match. The caller keeps the notice and shows no number — a
 * count that does not read is never invented.
 */
export function parseThemeBootstrapNoHistoryResult(
  resultJson: string | null
): ThemeBootstrapNoHistoryResult | null {
  return parseJsonColumn(themeBootstrapNoHistoryResultSchema, resultJson);
}

/**
 * Shown for a `ready` row whose result fails `themeBootstrapStoredResultSchema`
 * (never written): the screen offers «suggest again» instead of the proposal.
 */
export const THEME_BOOTSTRAP_UNREADABLE_CODE = 'unreadable';

/**
 * `error_code` of a `failed` attempt: `provider` — no answer (call,
 * timeout); `parse` — the answer is not in the agreed form; `no_themes` — the
 * answer is well-formed but holds no usable theme (an empty list, or every
 * candidate dropped by the checks); `unexpected` — a failure of our own around
 * the paid call. A failure is retried after an hour, so none of them locks the
 * organization for a day the way a stored `ready` would.
 *
 * Two codes are for attempts where NOTHING was paid
 * (`THEME_BOOTSTRAP_UNPAID_FAILURE_CODES`) and the row is claimable again at
 * once: `no_history` — no topic phrase could go to the model (none in the
 * window, the `topic` field off, or all withheld by redaction; the row keeps
 * `{ withheld }` in `result_json` so the screen can say how many); and
 * `not_started` — the call never happened (the Workflow was not started, the
 * provider was not ready, the history could not be read).
 */
export const THEME_BOOTSTRAP_FAILURE_CODES = [
  'provider',
  'parse',
  'no_themes',
  'unexpected',
  ...THEME_BOOTSTRAP_UNPAID_FAILURE_CODES,
] as const;
export type ThemeBootstrapFailureCode = (typeof THEME_BOOTSTRAP_FAILURE_CODES)[number];

// ============================================================================
// The model's answer (validated before anything of it is kept)
// ============================================================================

export const themeBootstrapModelThemeSchema = z.object({
  name: z.string(),
  definition: z.string(),
  // Any number: a stray non-integer or out-of-range number is dropped by the
  // parser, not the whole theme.
  phrases: z.array(z.number()),
});

export const themeBootstrapModelAnswerSchema = z.object({
  themes: z.array(z.unknown()),
});

// ============================================================================
// Internal route edge-api → workflows-api (service token)
// ============================================================================

/** `start` — a missing, stale or failed attempt; `regenerate` — also a `ready` one (24 h). */
export const themeBootstrapModeSchema = z.enum(['start', 'regenerate']);
export type ThemeBootstrapMode = z.infer<typeof themeBootstrapModeSchema>;

export const themeBootstrapServiceRequestSchema = z.object({
  orgId: z.string().min(1).max(255),
  userId: z.string().min(1).max(255).optional(),
  mode: themeBootstrapModeSchema,
  traceId: z.string().max(128).optional(),
});

/**
 * The params of `ThemeBootstrapWorkflow`, built by the workflows-api route from
 * a won claim and read back by the Workflow. Deliberately no data of the
 * conversations: the token, the slot key and the trace id only — the Workflow
 * state outlives the stored result's erasure, so no phrase may travel here.
 */
export const themeBootstrapWorkflowInputSchema = z
  .object({
    orgId: z.string().min(1).max(255),
    /** The claim token: fences the closing of the attempt, keys its cost row, fixes the history window. */
    requestedAt: z.string().min(1),
    /** The ceiling reservation of this press: given back when nothing is paid. */
    slotKey: z.string().min(1).max(128),
    traceId: z.string().max(128).optional(),
  })
  .strict();

export type ThemeBootstrapWorkflowInput = z.infer<
  typeof themeBootstrapWorkflowInputSchema
>;

export const THEME_BOOTSTRAP_REFUSALS = [
  'feature_disabled',
  'feature_unavailable',
  'over_limit_minute',
  'over_limit_hour',
  'over_limit_day',
  'limit_unavailable',
] as const;
export type ThemeBootstrapRefusal = (typeof THEME_BOOTSTRAP_REFUSALS)[number];

/**
 * What the press did. Edge-api answers the client with the attempt's state read
 * afterwards, so the outcome only decides the refusals.
 * - `running` — this request claimed the attempt and started the workflow
 *   that makes the call (`ThemeBootstrapWorkflow`); the generation goes on
 *   without the caller: edge-api answers the state read afterwards, normally
 *   `running`, and the screen polls;
 * - `not_claimed` — another request holds it, or its window has not passed;
 * - `refused` — a gate or the ceiling said no (`reason`).
 *
 * «Nothing to send» is not an outcome here: the history is read inside the
 * workflow, after the claim, and closes the attempt `failed/no_history` (see
 * `THEME_BOOTSTRAP_FAILURE_CODES`) — the notice comes from the state.
 */
export const themeBootstrapServiceResponseSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('running') }),
  z.object({ outcome: z.literal('not_claimed') }),
  z.object({ outcome: z.literal('refused'), reason: z.enum(THEME_BOOTSTRAP_REFUSALS) }),
]);

export type ThemeBootstrapServiceRequest = z.infer<
  typeof themeBootstrapServiceRequestSchema
>;
export type ThemeBootstrapServiceResponse = z.infer<
  typeof themeBootstrapServiceResponseSchema
>;

// ============================================================================
// Client routes
// ============================================================================

export const themeBootstrapProposalThemeSchema = z.object({
  name: z.string().min(1),
  definition: z.string(),
  needsDefinition: z.boolean(),
  /** Conversations of the theme in the window; `null` — the pairs are erased. */
  calls: z.number().int().min(0).nullable(),
});

export const themeBootstrapProposalSchema = z.object({
  windowStart: z.string(),
  windowEnd: z.string(),
  sampled: z.boolean(),
  /** The pairs are kept: the shares below are counted. */
  sharesAvailable: z.boolean(),
  /**
   * The shares were counted over a history read cut at the group cap (the
   * most frequent groups only): the numbers are a lower bound, and the screen
   * says so.
   */
  sharesApproximate: z.boolean(),
  /**
   * Why the shares are what they are: `ok` — counted; `erased` — the pairs are
   * gone (approved, regenerated, 30 days); `unavailable` — the pairs are kept
   * but the history could not be read now (transient, retry); `empty` — read,
   * but no conversation with a topic is left in the window.
   */
  sharesStatus: z.enum(['ok', 'erased', 'unavailable', 'empty']),
  /** Conversations with a topic in the window — the denominator. */
  totalCalls: z.number().int().min(0).nullable(),
  /** Of them — whose phrase went to the model («proposed from N %»). */
  coveredCalls: z.number().int().min(0).nullable(),
  /** Of them — in at least one theme; the rest is «no theme». */
  themedCalls: z.number().int().min(0).nullable(),
  themes: z.array(themeBootstrapProposalThemeSchema),
});

/**
 * What a press of `start` / `regenerate` found when it had nothing to send —
 * the state of the last attempt (`failed/no_history`), until the next press:
 * - `no_sendable_history` — no topic phrase could go to the model (none in the
 *   window, the `topic` field off, or all withheld by redaction: `withheld`
 *   conversations). Manual entry is the way on.
 */
export const themeBootstrapNoticeSchema = z.object({
  code: z.literal('no_sendable_history'),
  withheld: z.number().int().min(0),
});

/**
 * `GET …/bootstrap` and the answer of `start` / `regenerate`.
 * - `none` — never asked (or the organization has no row);
 * - `nextAttemptAt` — when a button may claim again: `failed` + 1 h, `running`
 *   + 15 min (abandoned), `ready` + 24 h (regenerate); `null` for `none`;
 * - `stale` — a `running` attempt older than the abandonment window: the
 *   server would accept a new claim, so the screen stops waiting and offers the
 *   button (the window is `nextAttemptAt` for `running`; this field spares the
 *   client from re-deriving it and, being a change of the answer, wakes a
 *   screen that only polls);
 * - `notice` — see `themeBootstrapNoticeSchema`: derived from a `failed` row
 *   with `no_history` (so it survives the poll that follows the press, and a
 *   reload); `null` everywhere else;
 * - a `ready` row whose result does not read is shown as `failed` with
 *   `THEME_BOOTSTRAP_UNREADABLE_CODE` and a retry available at once, never as
 *   approved;
 * - `historyCalls` — conversations with a topic in the last 90 days, read only
 *   when there is no proposal to show (`none`, `failed`): 0 — manual entry.
 */
export const themeBootstrapStateSchema = z.object({
  status: z.union([z.literal('none'), orgThemeBootstrapStatusSchema]),
  requestedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  nextAttemptAt: z.string().nullable(),
  errorCode: z.string().nullable(),
  approvedAt: z.string().nullable(),
  stale: z.boolean(),
  notice: themeBootstrapNoticeSchema.nullable(),
  historyCalls: z.number().int().min(0).nullable(),
  proposal: themeBootstrapProposalSchema.nullable(),
});

/**
 * Approve — the list as the admin edited it. `requestedAt` is the proposal's
 * claim token from the state the screen showed: a proposal regenerated
 * meanwhile answers 409 `bootstrap_changed` instead of creating themes the
 * admin never saw. Not `.strict()`, like the theme-set bodies.
 */
export const approveThemeBootstrapRequestSchema = z.object({
  requestedAt: z.string().min(1).max(40),
  themes: z
    .array(
      z.object({
        name: orgThemeNameSchema,
        definition: orgThemeDefinitionSchema.default(''),
      })
    )
    .min(1)
    .max(THEME_BOOTSTRAP_MAX_THEMES),
});

export const THEME_BOOTSTRAP_APPROVE_STATUSES = [
  'created',
  'duplicate',
  'archived',
  'set_full',
] as const;

/**
 * Per theme of the request, in its order: `created`; `duplicate` — the set
 * already has a theme with this name's key (a retried approval lands here:
 * idempotent), `theme` is that theme; `archived` — the key belongs to an
 * ARCHIVED theme (`theme` is it): nothing is created and nothing is restored,
 * the admin restores it by hand; `set_full` — the cap refused it.
 */
export const approveThemeBootstrapResponseSchema = z.object({
  results: z.array(
    z.object({
      name: z.string(),
      status: z.enum(THEME_BOOTSTRAP_APPROVE_STATUSES),
      theme: orgThemeSchema.optional(),
    })
  ),
  cap: z.number().int().min(1),
});

/**
 * Refusal codes of the bootstrap routes (body `{ error, code }`), next to the
 * theme-set gate codes they share:
 * - `bootstrap_not_ready` 409 — approve without a `ready` proposal;
 * - `bootstrap_changed` 409 — the proposal was regenerated since the screen read it;
 * - `bootstrap_already_approved` 409 — the proposal was approved already (a
 *   retry after a lost answer lands here; the themes it created stay);
 * - `over_limit` 429 — the generation ceiling of the organization;
 * - `temporarily_unavailable` 503 — a gate, the ceiling or the history unreadable;
 * - `bootstrap_failed` 503 — the generation service failed or is unreachable.
 */
export const THEME_BOOTSTRAP_ERROR_CODES = [
  'bootstrap_not_ready',
  'bootstrap_changed',
  'bootstrap_already_approved',
  'over_limit',
  'temporarily_unavailable',
  'bootstrap_failed',
  'tier_not_eligible',
  'feature_disabled',
  'themes_disabled',
  'theme_invalid',
] as const;

export const themeBootstrapErrorResponseSchema = z.object({
  error: z.string(),
  code: z.enum(THEME_BOOTSTRAP_ERROR_CODES),
});

export type ThemeBootstrapProposal = z.infer<typeof themeBootstrapProposalSchema>;
export type ThemeBootstrapProposalTheme = z.infer<
  typeof themeBootstrapProposalThemeSchema
>;
export type ThemeBootstrapNotice = z.infer<typeof themeBootstrapNoticeSchema>;
export type ThemeBootstrapState = z.infer<typeof themeBootstrapStateSchema>;
export type ApproveThemeBootstrapRequest = z.input<
  typeof approveThemeBootstrapRequestSchema
>;
export type ApproveThemeBootstrapResponse = z.infer<
  typeof approveThemeBootstrapResponseSchema
>;
export type ThemeBootstrapErrorCode = (typeof THEME_BOOTSTRAP_ERROR_CODES)[number];
